/**
 * Centralized client-side error logging.
 *
 * Why this exists:
 * - One place to forward errors to Sentry (which is lazy-loaded in main.tsx).
 * - One place to decide what to do in dev vs prod (today: always console.error
 *   so devtools still surface the stack; tomorrow we can gate by env).
 * - One signature across the app so refactors / lint rules can target it.
 *
 * Usage:
 *   import { logError } from "@/lib/logError";
 *
 *   try { ... } catch (err) {
 *     logError(err, { where: "useNotifications.fetch", userId });
 *   }
 *
 * Context is optional but encouraged — it becomes Sentry `extra` data and a
 * second arg to `console.error` so it's grep-able locally.
 *
 * Do NOT pass secrets / tokens in `context`. The Sentry `beforeSend` hook in
 * main.tsx redacts known header keys but cannot scrub arbitrary payloads.
 */

type ErrorContext = Record<string, unknown>;

// Sentry is loaded lazily after first paint; we hold a reference once ready
// so logError calls before init silently fall back to console.
let sentryRef: typeof import("@sentry/react") | null = null;

export function _registerSentry(s: typeof import("@sentry/react")): void {
  sentryRef = s;
}

function toError(value: unknown): Error {
  if (value instanceof Error) return value;
  if (typeof value === "string") return new Error(value);
  try {
    return new Error(JSON.stringify(value));
  } catch {
    return new Error(String(value));
  }
}

export function logError(error: unknown, context?: ErrorContext): void {
  const err = toError(error);

  // Always surface locally so devtools shows the stack.
  if (context) {
    // eslint-disable-next-line no-console
    console.error(err, context);
  } else {
    // eslint-disable-next-line no-console
    console.error(err);
  }

  if (sentryRef) {
    try {
      sentryRef.captureException(err, context ? { extra: context } : undefined);
    } catch {
      // Sentry failures must never break the caller.
    }
  }
}

export function logWarn(message: string, context?: ErrorContext): void {
  if (context) {
    // eslint-disable-next-line no-console
    console.warn(message, context);
  } else {
    // eslint-disable-next-line no-console
    console.warn(message);
  }

  if (sentryRef) {
    try {
      sentryRef.captureMessage(message, {
        level: "warning",
        extra: context,
      });
    } catch {
      // ignore
    }
  }
}
